import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { statisticMessages } from 'src/features/statistic/utils/messages';
import { useListUserActionEvents } from 'src/features/userAction/api/useListUserActionEvents';
import CloudUserBadge from 'src/features/userAction/components/CloudUserBadge';
import UserActionMessage from 'src/features/userAction/components/UserActionMessage';
import UserActionSidePanel from 'src/features/userAction/components/UserActionSidePanel';
import { userActionBadgeOptions, UserActionEvent } from 'src/features/userAction/utils/types';
import { CellSidePanelActions } from 'src/features/visitor/components/cell/CellSidePanelActions';
import { useParking } from 'src/providers/ParkingProvider';
import SidePanel from 'src/registry/components/sidePanel/SidePanel';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import { useSingleRowSelection } from 'src/registry/hooks/useSingleRowSelection';
import useSorting from 'src/registry/hooks/useSorting';
import { generalMessages } from 'src/registry/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';

const columnHelper = createColumnHelper<UserActionEvent>();
const initialSorting = { initialSortingState: [{ id: 'timestamp', desc: true }] };
export const USER_ACTIONS_PANEL_NAME = 'user-actions-panel';

export default function UserActions() {
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting(initialSorting);
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch();
    const { rowSelectionState, clearSelection, setRowSelectionState } = useSingleRowSelection();
    const { parkingId } = useParking();

    React.useEffect(() => {
        setTitle(formatMessage(statisticMessages.userActions));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data, isLoading } = useListUserActionEvents({
        parkingId,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });

    const singleRowSelection = React.useCallback(
        (rowId: string) => setRowSelectionState({ [rowId]: true }),
        [setRowSelectionState],
    );

    const eventId = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelectionState)[0];
        if (!data || !firstSelected) return '';
        return data.results.find((row) => row.id === firstSelected)?.id ?? '';
    }, [data, rowSelectionState]);

    const columns = [
        columnHelper.accessor('timestamp', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableSorting: true,
            meta: {
                title: formatMessage(statisticMessages.createdAt),
            },
        }),
        columnHelper.accessor('action_type', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(statisticMessages.type),
                badgeOptions: userActionBadgeOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: ({ row: { original } }) => {
                return (
                    <div className="flex flex-row gap-1">
                        <div>{original.name}</div>
                        <div>{!original.user && <CloudUserBadge />}</div>
                    </div>
                );
            },
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('email', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(statisticMessages.email),
            },
        }),
        columnHelper.accessor('event_data', {
            header: DataTableColumnHeader,
            cell: ({ row }) => (
                <div className="min-w-0">
                    <UserActionMessage userAction={row.original} />
                </div>
            ),
            meta: {
                title: formatMessage(statisticMessages.message),
            },
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props) => (
                <CellSidePanelActions
                    {...props}
                    sidePanelName={USER_ACTIONS_PANEL_NAME}
                    singleRowSelection={singleRowSelection}
                />
            ),
        }),
    ];

    return (
        <>
            <DataTable<UserActionEvent, any>
                isLoading={isLoading}
                columns={columns}
                sorting={sorting}
                filters={filters}
                search={search}
                data={data}
                pagination={pagination}
                semantics={{
                    title: formatMessage(statisticMessages.userActions),
                    entryName: formatMessage(statisticMessages.userAction).toLowerCase(),
                    entryNames: formatMessage(statisticMessages.userActions).toLowerCase(),
                    sidePanelName: USER_ACTIONS_PANEL_NAME,
                }}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
                getRowId={(row) => row.id.toString()}
            />
            <SidePanel onClose={clearSelection} name={USER_ACTIONS_PANEL_NAME}>
                <UserActionSidePanel eventId={eventId} />
            </SidePanel>
        </>
    );
}
